import { FACE_TEXTURE, PHOTO_INPUT } from '@kokun/shared';

export class PhotoError extends Error {}

/** Detecta el tipo REAL por bytes mágicos (no se confía en la extensión ni en file.type). */
export async function sniffImageType(file: Blob): Promise<'image/jpeg' | 'image/png' | 'image/webp' | null> {
  const b = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'image/png';
  const ascii = (s: number, e: number) => String.fromCharCode(...b.slice(s, e));
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';
  return null;
}

/** Valida y decodifica la foto localmente. Respeta la orientación EXIF. */
export async function loadPhoto(file: File): Promise<ImageBitmap> {
  if (file.size > PHOTO_INPUT.MAX_BYTES) throw new PhotoError(`La foto pesa demasiado (máx. ${PHOTO_INPUT.MAX_BYTES / 1024 / 1024} MB).`);
  const type = await sniffImageType(file);
  if (!type) throw new PhotoError('Formato no admitido. Usa una foto JPG, PNG o WebP.');
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new PhotoError('No se ha podido leer la imagen. ¿Está dañada?');
  }
  const { width, height } = bmp;
  if (width < PHOTO_INPUT.MIN_DIMENSION || height < PHOTO_INPUT.MIN_DIMENSION) {
    bmp.close();
    throw new PhotoError('La foto es demasiado pequeña (mínimo 128 px).');
  }
  if (width > PHOTO_INPUT.MAX_DIMENSION || height > PHOTO_INPUT.MAX_DIMENSION) {
    bmp.close();
    throw new PhotoError('La foto es demasiado grande (máximo 8000 px por lado).');
  }
  return bmp;
}

export interface Crop {
  /** Centro del óvalo en coordenadas de la imagen. */
  cx: number;
  cy: number;
  /** Lado del recuadro cuadrado en píxeles de la imagen. */
  size: number;
}

export interface FaceStyle {
  cartoon: boolean;
  brightness: number;
  saturation: number;
}

/** Intenta centrar automáticamente con FaceDetector (si el navegador lo ofrece). */
export async function autoCrop(img: ImageBitmap): Promise<Crop> {
  const fallback: Crop = { cx: img.width / 2, cy: img.height * 0.42, size: Math.min(img.width, img.height) * 0.7 };
  const FD = (window as unknown as { FaceDetector?: new (o: object) => { detect(i: ImageBitmap): Promise<Array<{ boundingBox: DOMRectReadOnly }>> } }).FaceDetector;
  if (!FD) return fallback;
  try {
    const faces = await new FD({ fastMode: true, maxDetectedFaces: 1 }).detect(img);
    const f = faces[0]?.boundingBox;
    if (!f) return fallback;
    return { cx: f.x + f.width / 2, cy: f.y + f.height * 0.48, size: Math.max(f.width, f.height) * 1.45 };
  } catch {
    return fallback;
  }
}

/**
 * Genera la textura facial de 256×256: recorte, máscara oval con bordes difuminados
 * (se funde con el color de piel del modelo) y filtro cartoon opcional. Al pasar por un canvas
 * se descartan los metadatos de la foto (EXIF, GPS…).
 */
export function renderFace(img: ImageBitmap, crop: Crop, style: FaceStyle): HTMLCanvasElement {
  const S = FACE_TEXTURE.SIZE;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.filter = `brightness(${style.brightness}) saturate(${style.saturation})${style.cartoon ? ' contrast(1.15)' : ''}`;
  ctx.drawImage(img, crop.cx - crop.size / 2, crop.cy - crop.size / 2, crop.size, crop.size, 0, 0, S, S);
  ctx.filter = 'none';

  if (style.cartoon) {
    // Posterizado suave: menos tonos = aspecto de ilustración
    const data = ctx.getImageData(0, 0, S, S);
    const d = data.data;
    const levels = 6;
    const step = 255 / (levels - 1);
    for (let i = 0; i < d.length; i += 4) {
      d[i] = Math.round(d[i] / step) * step;
      d[i + 1] = Math.round(d[i + 1] / step) * step;
      d[i + 2] = Math.round(d[i + 2] / step) * step;
    }
    ctx.putImageData(data, 0, 0);
  }

  // Máscara oval con degradado
  ctx.globalCompositeOperation = 'destination-in';
  const g = ctx.createRadialGradient(S / 2, S / 2, S * 0.3, S / 2, S / 2, S * 0.5);
  g.addColorStop(0, 'rgba(0,0,0,1)');
  g.addColorStop(0.8, 'rgba(0,0,0,0.9)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.save();
  ctx.translate(S / 2, S / 2);
  ctx.scale(0.86, 1);
  ctx.translate(-S / 2, -S / 2);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);
  ctx.restore();
  ctx.globalCompositeOperation = 'source-over';
  return c;
}

/** Codifica la textura para subirla (WebP si se puede; si no, PNG) respetando el tamaño máximo. */
export async function encodeFace(canvas: HTMLCanvasElement): Promise<Blob> {
  const toBlob = (type: string, q?: number) => new Promise<Blob | null>((r) => canvas.toBlob(r, type, q));
  for (const q of [0.85, 0.7, 0.55, 0.4]) {
    const b = await toBlob('image/webp', q);
    if (b && b.type === 'image/webp' && b.size <= FACE_TEXTURE.MAX_BYTES) return b;
    if (b && b.type !== 'image/webp') break;
  }
  const png = await toBlob('image/png');
  if (png && png.size <= FACE_TEXTURE.MAX_BYTES) return png;
  throw new PhotoError('No se ha podido comprimir la imagen lo suficiente.');
}
